import React, { useState, useEffect, useCallback } from 'react';
import { CreditCard, ExternalLink, Loader2, AlertCircle, RefreshCw, Infinity as InfinityIcon } from 'lucide-react';
import { api, openPricingPlans } from '../lib/api';

/**
 * Plan and usage screen.
 *
 * Shows what this app knows and Shopify does not -- how much of the monthly
 * label quota is left -- and links out for everything else.
 *
 * It deliberately does *not* render a plan catalogue. With Shopify App Pricing
 * the plan list, its prices and the whole upgrade/downgrade/cancel flow are a
 * Shopify-hosted page, and an app that reprints them is both redundant and
 * wrong the moment a price changes in the Partner Dashboard. Showing tiers with
 * "Switch to Basic" buttons also reads as the app running its own billing, which
 * is the appearance App Store requirement 1.2.1 exists to prevent. One link out
 * is the whole interface.
 *
 * The "How you are charged" card at the bottom exists for App Store requirement
 * 1.2.1 (no off-platform billing). This app has two costs sitting next to each
 * other -- the subscription, and Hrvatska Pošta's postage -- and only the first
 * is an *app charge*. The second is a carrier cost on the merchant's own HP
 * contract, which the app neither collects nor marks up. Left implicit, the pair
 * reads like off-platform billing; the card states which is which so neither a
 * merchant nor a reviewer has to infer it.
 */

/**
 * Symbols worth rendering. Deliberately incomplete: `plan.currency` is whatever
 * Shopify reports for the active subscription, so an unlisted code falls back to
 * printing the code itself rather than guessing or dropping it.
 */
const CURRENCY = { USD: '$', EUR: '€', GBP: '£' };


/**
 * `plan.price` is Shopify's own string ("5.00") whenever Shopify has quoted one,
 * and the catalogue's number only as a fallback. Parse before testing it: the
 * string "0.00" is truthy, so a bare falsy check would price a free plan as paid.
 */
function formatPrice(plan) {
  const amount = Number(plan.price);
  if (!Number.isFinite(amount) || amount <= 0) return 'Free';

  const symbol = CURRENCY[plan.currency];
  if (symbol) return `${symbol}${plan.price}/month`;
  return plan.currency ? `${plan.price} ${plan.currency}/month` : `${plan.price}/month`;
}

function formatResetDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
}

/** Green under 75%, amber approaching the cap, red once it is reached. */
function usageColor(used, limit) {
  if (limit === null) return '#008060';
  const ratio = limit ? used / limit : 1;
  if (ratio >= 1) return '#c5221f';
  if (ratio >= 0.75) return '#b98900';
  return '#008060';
}

function UsageBar({ usage }) {
  if (usage.unlimited) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#008060', fontWeight: 600 }}>
        <InfinityIcon size={18} /> <span>{usage.used} labels this month · no limit</span>
      </div>
    );
  }

  const pct = usage.limit ? Math.min(100, Math.round((usage.used / usage.limit) * 100)) : 100;
  const color = usageColor(usage.used, usage.limit);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.85rem',
          marginBottom: '0.4rem',
        }}
      >
        <span style={{ fontWeight: 600 }}>
          {usage.used} of {usage.limit} labels used
        </span>
        <span style={{ color: 'var(--color-secondary)' }}>{usage.remaining} left</span>
      </div>
      <div style={{ background: '#e1e3e5', borderRadius: '999px', height: '8px', overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
}

export default function Billing({ onPlanChanged }) {
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async ({ refresh = false } = {}) => {
    try {
      const { billing: loaded } = await api.getBilling({ refresh });
      setBilling(loaded);
      setError(null);
      onPlanChanged?.();
    } catch (err) {
      setError(err.message);
    }
  }, [onPlanChanged]);

  // Always read through to Shopify when this tab opens: the merchant most
  // likely arrived here straight back from the plan picker, and the
  // app_subscriptions/update webhook may still be in flight.
  useEffect(() => {
    load({ refresh: true }).finally(() => setLoading(false));
  }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await load({ refresh: true });
    setRefreshing(false);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <Loader2 size={32} className="spin" style={{ color: 'var(--color-brand-hp)' }} />
      </div>
    );
  }

  if (error && !billing) {
    return (
      <div
        style={{
          background: '#fce8e6',
          color: '#c5221f',
          padding: '0.75rem 1rem',
          borderRadius: '6px',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
        }}
      >
        <AlertCircle size={16} />
        <span>Could not load your plan: {error}</span>
      </div>
    );
  }

  const { plan, usage, subscription, upgradeUrl } = billing;
  const atLimit = !usage.unlimited && usage.remaining === 0;

  return (
    <div style={{ maxWidth: '900px', margin: '0 auto' }}>
      <div className="polaris-card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CreditCard size={18} color="#008060" />
            <h2 className="card-title">Your plan</h2>
          </div>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />} Refresh
          </button>
        </div>

        <div className="card-body">
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: '0.75rem',
              flexWrap: 'wrap',
              marginBottom: '1.25rem',
            }}
          >
            <span style={{ fontSize: '1.5rem', fontWeight: 700 }}>{plan.name}</span>
            <span style={{ color: 'var(--color-secondary)' }}>{formatPrice(plan)}</span>
            {subscription?.test && (
              <span className="badge badge-yellow">Test charge — not billed</span>
            )}
            {subscription?.status && subscription.status !== 'ACTIVE' && (
              <span className="badge badge-gray">{subscription.status}</span>
            )}
          </div>

          <UsageBar usage={usage} />

          <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', marginTop: '0.75rem' }}>
            {usage.unlimited
              ? 'Your plan has no monthly label limit.'
              : `Your allowance resets on ${formatResetDate(usage.periodEnd)}.`}
          </p>

          {atLimit && (
            <div
              style={{
                background: '#fce8e6',
                color: '#c5221f',
                padding: '0.75rem 1rem',
                borderRadius: '6px',
                fontSize: '0.85rem',
                marginTop: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <AlertCircle size={16} style={{ flexShrink: 0 }} />
              <span>
                You have used every label in the {plan.name} plan this month. New return labels are
                blocked until you upgrade or the allowance resets.
              </span>
            </div>
          )}

          <p style={{ fontSize: '0.78rem', color: 'var(--color-secondary)', marginTop: '0.75rem' }}>
            A label counts against your allowance when Hrvatska Pošta accepts it. Reprinting or
            tracking an existing label is always free.
          </p>

          <div style={{ marginTop: '1.25rem' }}>
            <button type="button" className="btn btn-primary" onClick={() => openPricingPlans(upgradeUrl)}>
              <ExternalLink size={16} /> View plans on Shopify
            </button>
            <p style={{ fontSize: '0.75rem', color: 'var(--color-secondary)', marginTop: '0.5rem' }}>
              Shopify hosts the plan page for this app: it lists every plan with its current price,
              and is where you upgrade, downgrade or cancel. Charges appear on your regular Shopify
              bill — this app never asks for payment details and cannot charge you anywhere else.
            </p>
          </div>
        </div>
      </div>

      <div className="polaris-card">
        <div className="card-header">
          <h2 className="card-title">How you are charged</h2>
        </div>
        <div className="card-body">
          <p style={{ fontSize: '0.85rem', marginTop: 0 }}>
            There are two separate costs involved in sending a return label, and this app collects
            neither of them directly.
          </p>

          <div className="charge-table-wrap" role="region" aria-label="Charge breakdown" tabIndex={0}>
            <table className="charge-table">
              <thead>
                <tr>
                  <th>What</th>
                  <th>Who bills you</th>
                  <th>Where it appears</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td data-label="What" className="table-primary-cell">
                    <strong>This app&rsquo;s subscription</strong>
                    <div style={{ color: 'var(--color-secondary)' }}>{formatPrice(plan)}</div>
                  </td>
                  <td data-label="Who bills you">Shopify</td>
                  <td data-label="Where it appears">Your Shopify bill</td>
                </tr>
                <tr>
                  <td data-label="What" className="table-primary-cell">
                    <strong>Postage</strong>
                    <div style={{ color: 'var(--color-secondary)' }}>
                      Your contracted Hrvatska Pošta rates
                    </div>
                  </td>
                  <td data-label="Who bills you">Hrvatska Pošta</td>
                  <td data-label="Where it appears">Your existing HP invoice</td>
                </tr>
              </tbody>
            </table>
          </div>

          <p style={{ fontSize: '0.8rem', color: 'var(--color-secondary)', marginTop: '1rem' }}>
            Every charge for this app goes through Shopify. The app takes no payment details, runs no
            payment processor of its own, and adds no fee or markup to postage. Postage is billed by
            Hrvatska Pošta under the carrier contract you hold with them directly, on the DXWebAPI
            account you entered in Settings — the same as if you had booked the shipment on HP&rsquo;s
            own system.
          </p>
        </div>
      </div>
    </div>
  );
}
